import React from 'react'
import config from '../../../config';
import { useDispatch, useSelector } from 'react-redux';
import { calculateAge, scrollToBottom, scrollToPercent } from '../../../utils/helpers';
import { setUser } from '../../../features/user/userSlice';
import { toast } from '../../common/Toast';
import axios from 'axios';
import { boostProfile } from '../../../features/user/userApi';
import BoostButton from '../../common/BoostButton';

// ------------------------------------------------------------
// DOB / Age helper
// ------------------------------------------------------------
// A DOB counts as "entered" only when day, month and year are all present and
// make sense. Without this check an empty year was treated as 0, so the age
// came out as the current year (e.g. 2026). Same rule as BasicInfoSection.jsx.
const isBlank = (v) => v === null || v === undefined || String(v).trim() === '';

function getAgeDisplay(year, month, day) {
  if (isBlank(day) || isBlank(month) || isBlank(year)) return '--';

  const d = Number(day);
  const m = Number(month);
  const y = Number(year);
  if (![d, m, y].every(Number.isInteger)) return '--';

  const currentYear = new Date().getFullYear();
  if (y < 1900 || y > currentYear) return '--';
  if (m < 1 || m > 12) return '--';
  if (d < 1 || d > 31) return '--';

  const age = calculateAge(year, month, day);
  const n = Number(age);
  if (age === null || age === undefined || age === '' || !Number.isFinite(n) || n < 0) {
    return '--';
  }
  return age;
}

// Trimmed: removed the "Edit Partner Profile" management link — it pointed
// at the partner tab, which was built around profiles.partner_preference,
// a column dropped in the matrimony cleanup (007_drop_matrimony_columns.sql).
// Added Blood Group and Disability to the info tables below — real columns
// (collected in signup's Basics & Lifestyle step) that weren't being shown
// anywhere on the profile before.
const Profile = ({onChangeTab}) => {
  const { userInfo, token } = useSelector(state => state.user);
  const dispatch =useDispatch()
    const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (15MB)
      if (file.size > 15 * 1024 * 1024) {
        toast.error('File size should be less than 15MB');
        return;
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        toast.error('Only JPG, PNG, or GIF files are allowed');
        return;
      }

      toast.error(null);
      const reader = new FileReader();
      reader.readAsDataURL(file);

      uploadProfileImage(file);
    }
  };

  const uploadProfileImage = async (file) => {
    const formData = new FormData();
    formData.append('profile', file);

    try {
      const response = await axios.post(`${config.baseURL}/api/profile/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
        }
      });

      toast.success("Upload successful")
       const updatedUser = {
      ...userInfo,
      profile_image: response.data.imageUrl,
    };

    dispatch(setUser({
      userInfo: updatedUser,
      token: token, // ← do NOT change token
    }));
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(
        error.response?.data?.message ||
        'Failed to upload image. Please try again.'
      );
    }
  };

  const ageDisplay = getAgeDisplay(
    userInfo?.birth_year,
    userInfo?.birth_month,
    userInfo?.birth_day
  );
  const heightDisplay = userInfo?.height || '--';

  return (
    <div>
      <div className="profile-name">
        <h4>
          {userInfo?.first_name} {userInfo?.last_name} <small className="text-muted">({userInfo?.profileId})</small>
        </h4>
      </div>
      <div className="border p-4 bg-white mt-3">
        <div className="row g-4">
          <div className="col-md-4 col-lg-3">
            <div className="upload-box text-center d-flex flex-column justify-content-center align-items-center rounded mb-2">
              {userInfo?.profile_image ? (<label
                htmlFor="fileUpload"
                className="d-flex flex-column justify-content-center align-items-center overflow-hidden"
              >
                <img className="object-fit-cover" src={userInfo?.profile_image ?`${config.baseURL}/uploads/profiles/${userInfo?.profile_image}`:"images/camera.png"}  />
              </label>):(<label
                htmlFor="fileUpload"
                className="d-flex flex-column justify-content-center align-items-center overflow-hidden"
              >
                <p>
                  <span className="click-here">Click here</span>
                  <br />
                  to upload
                </p>
                <img src={"images/camera.png"}  />
                <strong className="d-block mt-2">Photo</strong>
              </label>)}
              <input type="file" id="fileUpload" className="d-none" onChange={handleImageChange} accept="image/*"/>
            </div>
            {/* <BoostButton/> */}
          </div>
          <div className="col-md-8 col-lg-9">
            <div className="row py-3">
              <div className="col-md-6 pe-md-4">
                <table className="table table-borderless table-sm mini-data mb-0 ">
                  <tbody>
                    <tr>
                      <td>Age/Height:</td>
                      <td>{ageDisplay}/{heightDisplay}</td>
                    </tr>
                    <tr>
                      <td>Marital Status:</td>
                      <td>{userInfo?.marital_status || '--'}</td>
                    </tr>
                   
                  </tbody>
                </table>
              </div>

              <div className="col-md-6 border-start ps-md-4 mt-4 mt-md-0">
                <table className="table table-borderless table-sm mini-data mb-0">
                  <tbody>
                    <tr>
                      <td>Blood Group:</td>
                      <td>{userInfo?.blood_group || '--'}</td>
                    </tr>
                    <tr>
                      <td>Disability:</td>
                      <td>{userInfo?.disability || '--'}</td>
                    </tr>
                   
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-light p-3 rounded small">
              <div className="profile-management">
                <h4>
                  <strong>Manage your Profile</strong>
                </h4>
                <div className="profile-edit-list ff-manage-links">
                  {/* Draws each bullet itself, vertically centred with the link
                      text, and switches off the bullets from the global CSS
                      that were sitting too high. */}
                  <style>{`
                    .profile-edit-list.ff-manage-links li,
                    .profile-edit-list.ff-manage-links .row > div {
                      list-style: none;
                    }
                    .profile-edit-list.ff-manage-links .row > div::before,
                    .profile-edit-list.ff-manage-links .row > div::after,
                    .profile-edit-list.ff-manage-links a::after {
                      content: none !important;
                    }
                    .profile-edit-list.ff-manage-links a {
                      display: inline-flex;
                      align-items: center;
                      gap: 12px;
                      line-height: 1.4;
                      padding-left: 0;
                    }
                    .profile-edit-list.ff-manage-links a::before {
                      content: "";
                      display: inline-block;
                      position: static;
                      top: auto;
                      left: auto;
                      transform: none;
                      margin: 0;
                      width: 8px;
                      height: 8px;
                      flex: none;
                      border-radius: 50%;
                      background: #212529;
                    }
                  `}</style>
                  <div className="row mt-2">
                    <div className="col-6 col-md-3">
                      <a href="#" onClick={()=> scrollToPercent(22)}>Edit Personal Profile</a>
                    </div>
                    <div className="col-6 col-md-3">
                      <a href="#" onClick={()=>onChangeTab("settings")}>Set Contact Filters</a>
                    </div>
                    <div className="col-6 col-md-3">
                      <a href="#" onClick={()=>onChangeTab("settings")}>Hide / Delete Profile</a>
                    </div>
                    <div className="col-6 col-md-3">
                      <a href="#" onClick={()=> scrollToBottom()}>Edit Contact Details</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Profile